import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { TrendingUp, AlertTriangle, LineChart } from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import { Label } from "../ui/label";
import { Button } from "../ui/button";
import { api } from "../../lib/kostra";
import PlotlyChart from "../PlotlyChart";

export default function AnalysisPanel({ project }) {
  const [models, setModels] = useState({});
  const [responseKeys, setResponseKeys] = useState([]);
  const [activeKey, setActiveKey] = useState(null);
  const [xFactor, setXFactor] = useState(null);
  const [yFactor, setYFactor] = useState(null);
  const [grid, setGrid] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busyGrid, setBusyGrid] = useState(false);

  const factors = project.factors || [];
  const factorNames = factors.map((f) => f.name);

  const runAnalyze = async () => {
    if (!project.id) {
      toast.error("Save the project first");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post(`/rnd/projects/${project.id}/analyze`, {});
      setModels(data.models || {});
      setResponseKeys(data.response_keys || []);
      const firstOk = (data.response_keys || []).find((k) => data.models?.[k]?.ok);
      setActiveKey(firstOk || null);
      if (firstOk) {
        setXFactor(factorNames[0]);
        setYFactor(factorNames[1] || factorNames[0]);
      }
      toast.success("Models fitted");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Analysis failed");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!activeKey || !xFactor || !yFactor || xFactor === yFactor) {
      setGrid(null);
      return;
    }
    let cancelled = false;
    (async () => {
      setBusyGrid(true);
      try {
        const { data } = await api.post(`/rnd/projects/${project.id}/surface`, {
          response_key: activeKey, x_factor: xFactor, y_factor: yFactor, n: 30,
        });
        if (!cancelled) setGrid(data.grid);
      } catch (e) {
        if (!cancelled) setGrid(null);
      } finally {
        if (!cancelled) setBusyGrid(false);
      }
    })();
    return () => { cancelled = true; };
  }, [activeKey, xFactor, yFactor, project.id]);

  const activeModel = activeKey ? models[activeKey] : null;
  const hasModels = Object.keys(models).length > 0;
  const okKeys = responseKeys.filter((k) => models[k]?.ok);

  const contour = useMemo(() => {
    if (!grid) return null;
    return [{
      type: "contour", z: grid.z, x: grid.x_values, y: grid.y_values,
      colorscale: [
        [0.0, "#0A3B24"], [0.25, "#1F6B43"], [0.5, "#7A9E5C"],
        [0.75, "#D4AF37"], [1.0, "#FDF9EE"],
      ],
      contours: { coloring: "heatmap", showlabels: true, labelfont: { size: 10, color: "#FFFFFF" } },
      line: { width: 0.5 },
      colorbar: { title: { text: activeKey || "" }, thickness: 12 },
    }];
  }, [grid, activeKey]);

  const surface = useMemo(() => {
    if (!grid) return null;
    return [{
      type: "surface", z: grid.z, x: grid.x_values, y: grid.y_values,
      colorscale: [
        [0.0, "#0A3B24"], [0.5, "#7A9E5C"], [1.0, "#D4AF37"],
      ],
      contours: { z: { show: true, usecolormap: true, project: { z: true }, width: 1 } },
      colorbar: { title: { text: activeKey || "" }, thickness: 12 },
    }];
  }, [grid, activeKey]);

  return (
    <div className="space-y-5" data-testid="rnd-analysis-panel">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="kostra-section-title text-lg">Regression Analysis</h3>
          <p className="text-xs text-[#4B5563] mt-1">
            Fits a quadratic model in coded units for every numeric response. Need ≥ {1 + 2 * factors.length + (factors.length * (factors.length - 1)) / 2} valid trials.
          </p>
        </div>
        <Button onClick={runAnalyze} disabled={loading || !project.id || (project.trials || []).length === 0}
                className="bg-[#0A3B24] hover:bg-[#0E5232] text-white font-semibold"
                data-testid="rnd-analyze-btn">
          <TrendingUp size={16} className="mr-1.5" /> {loading ? "Fitting..." : "Run Analysis"}
        </Button>
      </div>

      {!hasModels && (
        <div className="rounded-md border-2 border-dashed border-[#E5E7EB] bg-[#F9FAFB] py-10 text-center">
          <LineChart size={28} className="mx-auto text-[#0A3B24]" />
          <div className="text-sm font-semibold text-[#111827] mt-2">No analysis yet</div>
          <div className="text-xs text-[#4B5563] mt-1">Fill at least the minimum trials with results, then click <strong>Run Analysis</strong>.</div>
        </div>
      )}

      {hasModels && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {responseKeys.map((k) => {
            const m = models[k];
            const ok = m?.ok;
            return (
              <button
                key={k}
                type="button"
                onClick={() => ok && setActiveKey(k)}
                className={`text-left rounded-lg border p-4 transition ${
                  activeKey === k && ok
                    ? "border-[#D4AF37] bg-[#FDF9EE] shadow"
                    : ok ? "border-[#E5E7EB] hover:border-[#0A3B24]/40 bg-white" : "border-[#FCDADA] bg-[#FFF7F7] cursor-not-allowed"
                }`}
                data-testid={`rnd-model-card-${k}`}
                disabled={!ok}
              >
                <div className="flex items-center justify-between">
                  <span className="kostra-data-label text-[#0A3B24]">{k}</span>
                  {!ok && <AlertTriangle size={12} className="text-[#DC2626]" />}
                </div>
                {ok ? (
                  <>
                    <div className="kostra-numeric-xl text-2xl text-[#0A3B24] mt-2">
                      R² {m.r_squared.toFixed(3)}
                    </div>
                    <div className="text-[11px] text-[#4B5563] mt-1">
                      Adj R² {m.adj_r_squared.toFixed(3)} · RMSE {m.rmse.toFixed(2)} · n={m.n_runs}
                    </div>
                  </>
                ) : (
                  <div className="text-xs text-[#991B1B] mt-2">{m?.reason}</div>
                )}
              </button>
            );
          })}
        </div>
      )}

      {activeModel?.ok && (
        <div className="space-y-4">
          <div className="kostra-card p-4">
            <h4 className="kostra-section-title text-sm">Equation (coded units, X<sub>i</sub> ∈ [-1, +1])</h4>
            <div className="font-mono-num text-xs mt-2 text-[#111827] bg-[#F9FAFB] p-3 rounded border break-words">
              <strong>Ŷ = </strong>{activeModel.equation_coded}
            </div>
            <details className="mt-3">
              <summary className="text-xs text-[#0A3B24] cursor-pointer font-semibold">View coefficient table & p-values</summary>
              <div className="mt-2 overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-[#0A3B24] text-white">
                    <tr><th className="py-1.5 px-2">Term</th><th className="px-2">Estimate</th><th className="px-2">SE</th><th className="px-2">t</th><th className="px-2">p</th></tr>
                  </thead>
                  <tbody>
                    {activeModel.coefficients.map((c) => (
                      <tr key={c.term} className="border-b">
                        <td className="py-1 px-2 font-mono-num">{c.term}</td>
                        <td className="px-2 text-right font-mono-num">{c.estimate.toFixed(4)}</td>
                        <td className="px-2 text-right font-mono-num">{c.std_error.toFixed(4)}</td>
                        <td className="px-2 text-right font-mono-num">{c.t.toFixed(2)}</td>
                        <td className={`px-2 text-right font-mono-num ${c.p_value < 0.05 ? "text-[#0A3B24] font-semibold" : "text-[#4B5563]"}`}>
                          {c.p_value.toFixed(4)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>
          </div>

          {factorNames.length >= 2 && (
            <div className="kostra-card p-4">
              <div className="flex flex-wrap items-end justify-between gap-3 mb-3">
                <div>
                  <h4 className="kostra-section-title text-sm">Response Surface — {activeKey}</h4>
                  <p className="text-xs text-[#4B5563]">Other factors held at mid-range. Pick any pair to inspect.</p>
                </div>
                <div className="flex items-end gap-2">
                  <div>
                    <Label className="kostra-data-label mb-1 block">X-axis factor</Label>
                    <Select value={xFactor || ""} onValueChange={setXFactor}>
                      <SelectTrigger className="h-9 w-44" data-testid="rnd-x-factor"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {factorNames.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="kostra-data-label mb-1 block">Y-axis factor</Label>
                    <Select value={yFactor || ""} onValueChange={setYFactor}>
                      <SelectTrigger className="h-9 w-44" data-testid="rnd-y-factor"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {factorNames.map((n) => <SelectItem key={n} value={n} disabled={n === xFactor}>{n}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {grid ? (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div className="h-[360px] rounded border bg-white" data-testid="rnd-contour-plot">
                    <PlotlyChart
                      data={contour}
                      layout={{
                        autosize: true,
                        margin: { l: 50, r: 30, t: 20, b: 45 },
                        xaxis: { title: { text: grid.x_name } },
                        yaxis: { title: { text: grid.y_name } },
                      }}
                    />
                  </div>
                  <div className="h-[360px] rounded border bg-white" data-testid="rnd-3d-plot">
                    <PlotlyChart
                      data={surface}
                      layout={{
                        autosize: true,
                        margin: { l: 0, r: 0, t: 20, b: 0 },
                        scene: {
                          xaxis: { title: { text: grid.x_name } },
                          yaxis: { title: { text: grid.y_name } },
                          zaxis: { title: { text: activeKey } },
                          camera: { eye: { x: 1.6, y: 1.4, z: 0.9 } },
                        },
                      }}
                    />
                  </div>
                </div>
              ) : (
                <div className="h-48 flex items-center justify-center text-xs text-[#4B5563]">
                  {busyGrid ? "Computing surface…" : "Pick two different factors above."}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
